import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Plus, Search, Trash2, Edit, Share2, ChevronUp, ChevronDown, LogOut, Settings, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

function AguaAdmin() {
  const [clientes, setClientes] = useState([]);
  const [config, setConfig] = useState({ nome_loja: 'LJ Águas', telefone_whatsapp: '', selos_para_premio: 10 });
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({ nome: '', telefone: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('agua_token');
    if (!token) {
      navigate('/agua/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientesRes, configRes] = await Promise.all([
        api.get('/agua/clientes'),
        api.get('/agua/config')
      ]);
      setClientes(clientesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCliente) {
        await api.put(`/agua/clientes/${editingCliente.id}`, formData);
        toast.success('Cliente atualizado!');
      } else {
        await api.post('/agua/clientes', formData);
        toast.success('Cliente cadastrado!');
      }
      setIsDialogOpen(false);
      setFormData({ nome: '', telefone: '' });
      setEditingCliente(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({ nome: cliente.nome, telefone: cliente.telefone });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await api.delete(`/agua/clientes/${id}`);
        toast.success('Cliente excluído');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir');
      }
    }
  };

  const handleAdicionarSelo = async (cliente) => {
    try {
      const { data } = await api.post(`/agua/clientes/${cliente.id}/adicionar-selo`);
      if (data.ganhou_premio) {
        toast.success(`🎉 ${cliente.nome} ganhou uma água grátis!`, { duration: 5000 });
      } else {
        toast.success(`Selo adicionado! ${data.selos}/${config.selos_para_premio}`);
      }
      loadData();
    } catch (error) {
      toast.error('Erro ao adicionar selo');
    }
  };

  const handleRemoverSelo = async (cliente) => {
    try {
      const { data } = await api.post(`/agua/clientes/${cliente.id}/remover-selo`);
      toast.success(`Selo removido! ${data.selos}/${config.selos_para_premio}`);
      loadData();
    } catch (error) {
      toast.error('Erro ao remover selo');
    }
  };

  const handleCompartilhar = (cliente) => {
    const url = `${window.location.origin}/agua/card/${cliente.id}`;
    const texto = `Olá ${cliente.nome}! 💧\n\nAcesse seu cartão fidelidade ${config.nome_loja}:\n${url}\n\nVocê tem ${cliente.selos}/${config.selos_para_premio} selos!`;
    
    const telefone = cliente.telefone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${telefone}?text=${encodeURIComponent(texto)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await api.post('/agua/config', config);
      toast.success('Configurações salvas!');
      setIsConfigOpen(false);
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('agua_token');
    localStorage.removeItem('agua_user');
    navigate('/');
  };

  const clientesFiltrados = clientes.filter(c =>
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.telefone.includes(searchTerm)
  );

  const user = JSON.parse(localStorage.getItem('agua_user') || '{}');

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-900 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center">
              <Droplets className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{config.nome_loja}</h1>
              <p className="text-xs text-cyan-300">Painel Administrativo</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-white/70 hidden sm:block">Olá, {user.nome}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsConfigOpen(true)}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-xs text-cyan-300 uppercase">Total Clientes</p>
            <p className="text-2xl font-bold text-white">{clientes.length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-xs text-cyan-300 uppercase">Selos p/ Prêmio</p>
            <p className="text-2xl font-bold text-white">{config.selos_para_premio}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-xs text-cyan-300 uppercase">Próx. do Prêmio</p>
            <p className="text-2xl font-bold text-white">
              {clientes.filter(c => c.selos >= config.selos_para_premio - 2).length}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-xs text-cyan-300 uppercase">Total Selos</p>
            <p className="text-2xl font-bold text-white">
              {clientes.reduce((acc, c) => acc + c.selos, 0)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar cliente..."
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pl-10"
            />
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingCliente(null);
              setFormData({ nome: '', telefone: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>{editingCliente ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-cyan-100">Nome</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-cyan-100">Telefone (WhatsApp)</Label>
                  <Input
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="(00) 00000-0000"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-white/70">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                    {editingCliente ? 'Salvar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lista de Clientes */}
        <div className="space-y-3">
          {clientesFiltrados.length === 0 ? (
            <div className="bg-white/5 rounded-xl p-8 text-center border border-white/10">
              <Users className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">Nenhum cliente cadastrado</p>
            </div>
          ) : (
            clientesFiltrados.map((cliente, index) => (
              <motion.div
                key={cliente.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-cyan-500/30 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{cliente.nome}</h3>
                    <p className="text-sm text-cyan-300">{cliente.telefone}</p>
                  </div>

                  {/* Selos */}
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoverSelo(cliente)}
                        disabled={cliente.selos === 0}
                        className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <div className="bg-cyan-600/30 px-4 py-2 rounded-lg min-w-[80px] text-center">
                        <span className="text-xl font-bold text-white">{cliente.selos}</span>
                        <span className="text-white/70">/{config.selos_para_premio}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAdicionarSelo(cliente)}
                        className="text-white/70 hover:text-white hover:bg-cyan-500/20 h-8 w-8 p-0"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCompartilhar(cliente)}
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-8 w-8 p-0"
                        title="Enviar cartão via WhatsApp"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(cliente)}
                        className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(cliente.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 transition-all duration-300"
                      style={{ width: `${(cliente.selos / config.selos_para_premio) * 100}%` }}
                    />
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </main>

      {/* Dialog Configurações */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Configurações</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveConfig} className="space-y-4">
            <div>
              <Label className="text-cyan-100">Nome da Loja</Label>
              <Input
                value={config.nome_loja}
                onChange={(e) => setConfig({ ...config, nome_loja: e.target.value })}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <Label className="text-cyan-100">WhatsApp da Loja</Label>
              <Input
                value={config.telefone_whatsapp}
                onChange={(e) => setConfig({ ...config, telefone_whatsapp: e.target.value })}
                className="bg-white/10 border-white/20 text-white"
                placeholder="(00) 00000-0000"
              />
            </div>
            <div>
              <Label className="text-cyan-100">Selos para Prêmio</Label>
              <Input
                type="number"
                value={config.selos_para_premio}
                onChange={(e) => setConfig({ ...config, selos_para_premio: parseInt(e.target.value) || 10 })}
                className="bg-white/10 border-white/20 text-white"
                min="1"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="ghost" onClick={() => setIsConfigOpen(false)} className="text-white/70">
                Cancelar
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AguaAdmin;
